import { CloseIcon } from "@givenwell/core-solid-icons";
import { CSS, keyframes, styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { Dialog as DialogPrimitive } from "radix-ui";
import { ReactNode } from "react";
import { Box, Flex } from "./layout";

export type AlertDialogProps = DialogPrimitive.DialogProps & {
  children?: ReactNode;
  title?: ReactNode;
  content?: ReactNode;
  footer?: ReactNode;
  css?: CSS;
  animation?: "scale" | "none";
};

export function AlertDialog({
  children,
  title,
  content,
  footer,
  css,
  animation = "scale",
  ...props
}: AlertDialogProps) {
  return (
    <DialogPrimitive.Root {...props}>
      <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <Overlay>
          <Box css={{ flex: "2 0 0px" }} />
          <Content animation={animation} css={css}>
            <Header>
              <Title>{title}</Title>
              <Flex css={{ flex: "1 0 0px" }}></Flex>
              <Close>
                <CloseIcon />
              </Close>
            </Header>
            <Body>{content}</Body>
            {footer && <Footer>{footer}</Footer>}
          </Content>
          <Box css={{ flex: "3 0 0px" }} />
        </Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

const scaleIn = keyframes({
  from: { transform: "scale(0.95)" },
  to: { transform: "scale(1)" },
});

const scaleOut = keyframes({
  from: { transform: "scale(1)" },
  to: { transform: "scale(0.95)" },
});

const Overlay = styled(DialogPrimitive.Overlay, {
  bg: colors.navy + "80",
  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  overflow: "auto",
  px: 20,
  pb: 20,
  pt: 64,
  animation: `${fadeIn()} 0.15s ease-in-out`,
  "&[data-state='closed']": {
    animation: `${fadeOut()} 0.15s ease-in-out`,
  },
  zIndex: 2147483647,
});

const noOp = keyframes({
  from: { opacity: 1 },
  to: { opacity: 1 },
});

const Content = styled(DialogPrimitive.Content, {
  bg: colors.cream,
  w: "100%",
  maxW: 390,
  rounded: 12,
  transformOrigin: "top",
  "&[data-state='closed']": {
    animation: `${noOp()} 0.15s ease-in`,
  },
  variants: {
    animation: {
      none: {},
      scale: {
        animation: `${scaleIn()} 0.15s ease-out`,
        "&[data-state='closed']": {
          animation: `${scaleOut()} 0.15s ease-in`,
        },
      },
    },
  },
});

const Header = styled("div", {
  d: "flex",
  minH: 64,
  items: "flex-start",
  px: 4,
  pl: 16,
});
const Body = styled("div", {
  px: 16,
  pb: 16,
});
const Footer = styled("div", {
  px: 16,
  pb: 16,
  pt: 8,
  d: "flex",
  justify: "flex-end",
  gap: 8,
});

const Close = styled(DialogPrimitive.Close, {
  size: 48,
  minSize: 48,
  mt: 8,
  d: "flex",
  items: "center",
  justify: "center",
});
const Title = styled(DialogPrimitive.Title, {
  fontScale: "xl",
  fontWeight: 700,
  py: 16,
  fontFamily: fonts.ftHabit,
});

export function AlertDialogClose(props: DialogPrimitive.DialogCloseProps) {
  return <DialogPrimitive.Close asChild {...props} />;
}

export const AlertDialogDescription = styled(DialogPrimitive.Description, {
  color: colors.text2,
  fontScale: "md",
});
